<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { summarizeForUser, type NormalizedError } from '@/errors';
import { routeConstants } from '@/utils';
import { ButtonType, CustomButton } from '@/components/common/';
import SpData from '@/assets/sp-data-icon.svg';

/**
 * The fatal tier: replaces the router outlet when the view is unusable.
 *
 * Rendered from store state rather than an `/error` route on purpose — a route needs a working
 * router, and the router is one of the things that breaks. This way the URL stays intact, so retry
 * and reload both do the right thing.
 *
 * Layout follows `src/components/NotFound.vue` so the app's two full-page messages look alike.
 */
const props = defineProps({
  error: {
    type: Object as PropType<NormalizedError>,
    required: true
  },
  retrying: {
    type: Boolean,
    default: false
  },
  showHome: {
    type: Boolean,
    default: true
  }
});

const emits = defineEmits(['retry', 'report']);

const { t: $t } = useI18n();

const summary = computed(() => summarizeForUser(props.error));
</script>

<template>
  <div class="error-screen">
    <img alt="Studypages Data" :src="SpData" class="error-screen-logo" />
    <FontAwesomeIcon :icon="['far', 'exclamation-triangle']" class="error-screen-icon" />
    <h1 class="error-screen-title">{{ summary.title }}</h1>
    <p class="error-screen-subtitle">{{ summary.message }}</p>
    <p class="error-screen-reference">
      {{ $t('error_reference') }} <code>{{ summary.reference }}</code>
    </p>
    <div class="error-screen-actions">
      <CustomButton :text="$t('error_retry')"
        :loading="retrying"
        :type="ButtonType.OUTLINED"
        @onClick="emits('retry')"/>
      <CustomButton :text="$t('error_report_problem')"
        v-if="error.isReportable"
        :margin-left="12"
        :type="ButtonType.WITHOUT_OUTLINE"
        @onClick="emits('report')"/>
    </div>
    <router-link :to="{ name: routeConstants.ROOT.name }" class="error-screen-link" v-if="showHome">
      {{ $t('go_to_home') }}
    </router-link>
  </div>
</template>

<style scoped lang="scss">
.error-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100vh;
  padding: 24px;
  text-align: center;

  &-logo {
    max-width: 64px;
    margin-bottom: 24px;
  }

  &-icon {
    font-size: 40px;
    color: #6b7075;
  }

  &-title {
    margin: 16px 0 0;
    font-size: 24px;
    color: #272a2e;
  }

  &-subtitle {
    margin: 8px 0 0;
    max-width: 48ch;
    font-size: 14px;
    color: #6b7075;
  }

  &-reference {
    margin: 12px 0 0;
    font-size: 12px;
    color: #6b7075;

    code {
      font-size: 12px;
      letter-spacing: 0.04em;
    }
  }

  &-actions {
    display: flex;
    margin-top: 24px;
  }

  &-link {
    margin-top: 24px;
    font-size: 13px;
    color: #6b7075;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
}
</style>
